import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, Result, Button, Modal } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { APP_VERSION } from './config/version';
import './styles/responsive.css';

// 页面组件
import LoginPage from './pages/LoginPage';
import SsoPage from './pages/SsoPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import UploadPage from './pages/UploadPage';
import SalesTours from './pages/SalesTours';
import SalesTourists from './pages/SalesTourists';
import InvoiceRecords from './pages/InvoiceRecords';
import TouristNotice from './pages/TouristNotice';
import AddOnNotice from './pages/AddOnNotice';
import OCRTest from './pages/OCRTest';

// 布局组件
import AdminLayout from './layouts/AdminLayout';
import SalesLayout from './layouts/SalesLayout';

// 工具组件
import ProtectedRoute from './components/ProtectedRoute';
import authService from './utils/auth';
import './App.css';

// 未授权页面
function UnauthorizedPage() {
  const handleGoBack = () => {
    const userInfo = authService.getUserInfo();
    if (userInfo?.role === 'admin' || userInfo?.role === 'operations') {
      window.location.href = '/admin';
    } else if (userInfo?.role === 'finance') {
      window.location.href = '/admin';
    } else if (userInfo?.role === 'salesperson') {
      window.location.href = '/sales';
    } else if (userInfo?.role === 'guide') {
      window.location.href = '/login';
    } else {
      window.location.href = '/login';
    }
  };

  return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Result
        status="403"
        title="403"
        subTitle="抱歉，您没有权限访问此页面"
        extra={<Button type="primary" onClick={handleGoBack}>返回首页</Button>}
      />
    </div>
  );
}

// 首页重定向组件
function HomeRedirect() {
  const userInfo = authService.getUserInfo();

  if (!authService.isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  // 财务角色跳转到账单统计
  if (userInfo?.role === 'finance') {
    return <Navigate to="/admin/billing" replace />;
  }

  // 管理员和运营跳转到管理后台
  if (userInfo?.role === 'admin' || userInfo?.role === 'operations') {
    // 根据权限跳转到第一个可用页面
    const permissions = userInfo.permissions || [];
    if (permissions.includes('tours')) {
      return <Navigate to="/admin/tours" replace />;
    }
    if (permissions.includes('tourists')) {
      return <Navigate to="/admin/tourists" replace />;
    }
    if (permissions.includes('salespersons')) {
      return <Navigate to="/admin/salespersons" replace />;
    }
    if (permissions.includes('ocr-logs')) {
      return <Navigate to="/admin/ocr-logs" replace />;
    }
    if (permissions.includes('notice')) {
      return <Navigate to="/admin/destinations" replace />;
    }
    // 默认跳转到产品管理
    return <Navigate to="/admin/tours" replace />;
  }

  // 销售人员跳转到销售端
  if (userInfo?.role === 'salesperson') {
    return <Navigate to="/sales/tours" replace />;
  }

  // 导游暂无专属页面，跳转到登录页
  if (userInfo?.role === 'guide') {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to="/login" replace />;
}

function App() {
  const [versionChecked, setVersionChecked] = useState(false);

  useEffect(() => {
    // 公开路由列表 - 这些页面不需要版本检查和登录提示
    const publicPaths = ['/notice/', '/addon-notice/', '/upload/', '/login', '/forgot-password', '/reset-password'];
    const currentPath = window.location.pathname;
    const isPublicRoute = publicPaths.some(path => currentPath.startsWith(path));

    // 只在非公开路由时检查版本
    if (!isPublicRoute) {
      const needsRelogin = authService.checkVersionAndLogout();
      if (needsRelogin) {
        window.location.href = '/login';
        return;
      }
    }
    setVersionChecked(true);
  }, []);

  if (!versionChecked) {
    return null; // 等待版本检查完成
  }

  return (
    <ConfigProvider locale={zhCN}>
      <Router>
        <Routes>
          {/* 公开路由 */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/sso" element={<SsoPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/upload/:uploadLink" element={<UploadPage />} />
          <Route path="/notice/:noticeLink" element={<TouristNotice />} />
          <Route path="/addon-notice/:addOnNoticeLink" element={<AddOnNotice />} />
          <Route path="/ocr-test" element={<OCRTest />} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          
          {/* 管理后台路由（管理员和运营） */}
          <Route path="/admin/*" element={
            <ProtectedRoute requireStaff={true}>
              <AdminLayout />
            </ProtectedRoute>
          } />
          
          {/* 销售端路由 */}
          <Route path="/sales" element={
            <ProtectedRoute>
              <SalesLayout />
            </ProtectedRoute>
          }>
            <Route index element={<Navigate to="/sales/tours" replace />} />
            <Route path="tours" element={<SalesTours />} />
            <Route path="tourists" element={<SalesTourists />} />
            <Route path="invoices" element={<InvoiceRecords />} />
          </Route>
          
          {/* 首页和默认路由 */}
          <Route path="/" element={<HomeRedirect />} />
          
          {/* 404页面 */}
          <Route path="*" element={
            <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Result
                status="404"
                title="404"
                subTitle="抱歉，您访问的页面不存在"
                extra={<Button type="primary" onClick={() => window.location.href = '/'}>返回首页</Button>}
              />
            </div>
          } />
        </Routes>
      </Router>
    </ConfigProvider>
  );
}

export default App;